import { clsx } from 'clsx';
import { Checkbox, Input, Select } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import { CxSurveyQuestionProps, OptionItemProps, SurveyQuestionType } from '../Survey';
import { SurveyMultipleOptions } from '@pages/admin-settings/Survey/SurveyMultipleOptions';
import gCls from '../Survey.module.css';
import cls from './SurveyRow.module.css';


interface Props {
  item: CxSurveyQuestionProps;
  list: CxSurveyQuestionProps[];
  setList: (question: CxSurveyQuestionProps[]) => void;
  hasNewRow: boolean;
  setHasNewRow: (flag: boolean) => void;
  hasAlterName?: boolean;
  hasMultiple?: boolean;
}

export const SurveyRow = (
  { item, list, setList, hasNewRow, setHasNewRow, hasAlterName, hasMultiple }: Props,
) => {
  const onTypeChange = (type: SurveyQuestionType) => {
    let options: OptionItemProps[] | null = [
      { value: 'yes', label: 'Sí', idx: 'yes' },
      { value: 'no', label: 'No', idx: 'no' },
    ];
    if (type === '1-5') {
      options = [
        { value: '1', label: '1', idx: '1' },
        { value: '2', label: '2', idx: '2' },
        { value: '3', label: '3', idx: '3' },
        { value: '4', label: '4', idx: '4' },
        { value: '5', label: '5', idx: '5' },
      ];
    } else if (type === 'options') {
      options = [];
    } else if (type === 'text') {
      options = null;
    }
    setList(list.map(i => i.idx === item.idx ? { ...i, type, options } : i));
  };
  const options = [
    { value: '1-5', label: '1-5' },
    { value: 'yes-no', label: 'Yes/No' },
    { value: 'text', label: 'Open' },
  ];

  if (hasMultiple) {
    options.push({ value: 'options', label: 'Multiple' });
  }

  const changePosition = (isDown = false) => {
    if ((isDown && item.position === list.length) || (!isDown && item.position <= 1)) return;
    const currentPosition = item.position;
    const siblingItem = list.find((i) => (
      i.position === (isDown ? currentPosition + 1 : currentPosition - 1)
    ));
    if (!siblingItem) return;
    const otherItems = list
      .filter((i) => i.position !== currentPosition && i.position !== siblingItem.position);
    setList([
      ...otherItems,
      { ...item, position: (isDown ? currentPosition + 1 : currentPosition - 1) },
      { ...siblingItem, position: currentPosition },
    ].sort((a, b) => a.position - b.position));
  };

  const onDataChange = (e: string | boolean, key: string) => {
    setList(list.map(i => (i.idx === item.idx ? { ...i, [key]: e } : i)));
  };

  const onOptionsChange = (itemIdx: string, options: OptionItemProps[]) => {
    setList(list.map(i => i.idx === itemIdx ? { ...i, options } : i));
  };

  const onSave = () => {
    setHasNewRow(false);
  };

  const onCancel = () => {
    setList(list.filter(i => i.idx !== item.idx));
    setHasNewRow(false);
  };

  return (
    <div className={gCls.tr}>
      <div className={clsx(gCls.td, gCls.tdCheck)}>
        {hasNewRow && item.position === list.length ? (
          <button
            className={clsx(cls.btn)}
            onClick={onSave}
            disabled={!item.name.trim() || (hasAlterName ? !item.nameClient.trim() : false)}
          >
            <CheckOutlined className={cls.btnIcon} />
          </button>
        ) : (
          <Checkbox
            checked={item.isSelected}
            onChange={e => onDataChange(e.target.checked, 'isSelected')}
            className={cls.checkbox}
            disabled={hasNewRow}
          />
        )}
      </div>
      <div className={clsx(gCls.td, gCls.tdPosition)}>
        {hasNewRow && item.position === list.length ? (
          <button className={clsx(cls.btn, cls.btnAlert)} onClick={onCancel}>
            <CloseOutlined className={cls.btnIcon} />
          </button>
        ) : (
          <div className={cls.positionButtons}>
            <button
              className={clsx(cls.positionBtn, cls.positionBtnUp)}
              onClick={() => changePosition()}
              disabled={hasNewRow || item.position === 1}
            />
            <button
              className={clsx(cls.positionBtn, cls.positionBtnDown)}
              onClick={() => changePosition(true)}
              disabled={hasNewRow || item.position === list.length}
            />
          </div>
        )}
      </div>
      <div className={clsx(gCls.td, gCls.tdQuestionCx)}>
        <Input
          value={item.name}
          className={clsx(cls.textField, !hasAlterName && cls.textFieldFull)}
          bordered={false}
          onChange={e => onDataChange(e.target.value, 'name')} />
      </div>
      {hasAlterName ? (
        <div className={clsx(gCls.td, gCls.tdQuestionSurvey)}>
          <Input
            value={item.nameClient}
            className={cls.textField}
            bordered={false}
            onChange={e => onDataChange(e.target.value, 'nameClient')} />
        </div>
      ) : null}
      <div className={clsx(gCls.td, gCls.tdType)}>
        <Select
          value={item.type}
          defaultValue="yes-no"
          style={{ width: 120 }}
          onChange={onTypeChange}
          className={cls.select}
          size={'small'}
          options={options}
        />
      </div>
      <div className={clsx(gCls.td, gCls.tdRequired)}>
        <Checkbox
          checked={item.isRequired}
          onChange={e => onDataChange(e.target.checked, 'isRequired')}
          className={cls.checkbox}
        />

        {item.type === 'options' ? (
          <div className={clsx(gCls.float)}>
            <SurveyMultipleOptions question={item} onOptionsChange={onOptionsChange} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
